import React, { useEffect, useState } from 'react';
import {
  MessageCircle, Mail, Phone, StickyNote, Calendar, ListChecks, Trophy,
  X, UserPlus, ArrowRight, Inbox, Bot,
} from 'lucide-react';
import clsx from 'clsx';
import { activitiesService, Activity, ActivityKind, ActivityEntityType } from '@/services/activities.service';

interface Props {
  entityType: ActivityEntityType;
  entityUid: string;
  /** Optional cap on number of rows fetched. Default 50. */
  limit?: number;
}

interface KindMeta {
  label: string;
  icon: React.ReactNode;
  className: string;
}

const KIND: Record<ActivityKind, KindMeta> = {
  whatsapp_in:       { label: 'Inbound WhatsApp',  icon: <MessageCircle size={12} />, className: 'bg-success-soft text-success' },
  whatsapp_out:      { label: 'Outbound WhatsApp', icon: <MessageCircle size={12} />, className: 'bg-primary-soft text-primary' },
  email_in:          { label: 'Inbound email',     icon: <Mail size={12} />,          className: 'bg-success-soft text-success' },
  email_out:         { label: 'Outbound email',    icon: <Mail size={12} />,          className: 'bg-info-soft text-info' },
  sms_in:            { label: 'Inbound SMS',       icon: <MessageCircle size={12} />, className: 'bg-success-soft text-success' },
  sms_out:           { label: 'Outbound SMS',      icon: <MessageCircle size={12} />, className: 'bg-info-soft text-info' },
  call_in:           { label: 'Incoming call',     icon: <Phone size={12} />,         className: 'bg-success-soft text-success' },
  call_out:          { label: 'Outgoing call',     icon: <Phone size={12} />,         className: 'bg-info-soft text-info' },
  note:              { label: 'Note',              icon: <StickyNote size={12} />,    className: 'bg-warning-soft text-warning' },
  meeting:           { label: 'Meeting',           icon: <Calendar size={12} />,      className: 'bg-info-soft text-info' },
  task_created:      { label: 'Task created',      icon: <ListChecks size={12} />,    className: 'bg-surface-alt text-text-muted' },
  task_completed:    { label: 'Task completed',    icon: <ListChecks size={12} />,    className: 'bg-success-soft text-success' },
  deal_created:      { label: 'Deal created',      icon: <Trophy size={12} />,        className: 'bg-primary-soft text-primary' },
  deal_stage_change: { label: 'Stage changed',     icon: <ArrowRight size={12} />,    className: 'bg-info-soft text-info' },
  deal_won:          { label: 'Deal won',          icon: <Trophy size={12} />,        className: 'bg-success-soft text-success' },
  deal_lost:         { label: 'Deal lost',         icon: <X size={12} />,             className: 'bg-danger-soft text-danger' },
  lead_received:     { label: 'Lead received',     icon: <Inbox size={12} />,         className: 'bg-info-soft text-info' },
  lead_assigned:     { label: 'Lead assigned',     icon: <UserPlus size={12} />,      className: 'bg-warning-soft text-warning' },
  system:            { label: 'System',            icon: <Bot size={12} />,           className: 'bg-surface-alt text-text-muted' },
};

function formatTimestamp(iso: string): string {
  const d = new Date(iso);
  const diffMs = Date.now() - d.getTime();
  const min = Math.floor(diffMs / 60000);
  if (min < 1) return 'just now';
  if (min < 60) return `${min} min ago`;
  const hr = Math.floor(min / 60);
  if (hr < 24) return `${hr} hour${hr === 1 ? '' : 's'} ago`;
  const day = Math.floor(hr / 24);
  if (day < 7) return `${day} day${day === 1 ? '' : 's'} ago`;
  return d.toLocaleDateString([], { month: 'short', day: 'numeric', year: 'numeric' });
}

export const ActivityTimeline: React.FC<Props> = ({ entityType, entityUid, limit = 50 }) => {
  const [items, setItems] = useState<Activity[] | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    activitiesService.timeline(entityType, entityUid, { limit })
      .then((data) => { if (!cancelled) setItems(data); })
      .catch(() => { if (!cancelled) setItems([]); })
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, [entityType, entityUid, limit]);

  if (loading) {
    return (
      <div className="space-y-2">
        {[0, 1, 2].map((i) => <div key={i} className="skeleton h-12 rounded-lg" />)}
      </div>
    );
  }

  if (!items || items.length === 0) {
    return (
      <div className="rounded-lg border border-dashed border-border px-4 py-8 text-center text-xs text-text-muted">
        No activity yet. Messages, notes, and deal changes will appear here.
      </div>
    );
  }

  return (
    <div className="space-y-3">
      {items.map((a) => {
        const meta = KIND[a.kind] ?? KIND.system;
        return (
          <div key={a.activityUid} className="flex gap-3">
            <div className={clsx('mt-0.5 flex h-7 w-7 shrink-0 items-center justify-center rounded-full', meta.className)}>
              {meta.icon}
            </div>
            <div className="min-w-0 flex-1">
              <div className="flex items-baseline justify-between gap-3">
                <span className="text-xs font-semibold text-text-primary">{meta.label}</span>
                <span className="shrink-0 text-2xs text-text-muted" title={new Date(a.createdAt).toLocaleString()}>
                  {formatTimestamp(a.createdAt)}
                </span>
              </div>
              {a.summary && (
                <p className="mt-0.5 text-xs text-text-secondary leading-relaxed whitespace-pre-line">{a.summary}</p>
              )}
              {a.actorKind === 'bot' && (
                <span className="mt-0.5 inline-flex items-center gap-1 text-2xs text-text-muted">
                  <Bot size={9} /> automated
                </span>
              )}
            </div>
          </div>
        );
      })}
    </div>
  );
};
